import React from "react";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import YouTubeIcon from "@mui/icons-material/YouTube";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import LanguageIcon from "@mui/icons-material/Language";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import { useNavigate } from "react-router-dom";
import { useLanding } from "../context/LandingContext";
import { useProjectConfig } from "../context/ProjectConfigContext";
import {
  buildWhatsAppUrl,
  fullBusinessAddress,
  resolveLandingMedia,
} from "../utils/landingMedia";
import BrandLogoImage from "./BrandLogoImage";
import { openConfiguredUrl } from "../utils/configuredNavigation";
import { hasVisibleLandingSegmentType, resolveFooterStyle } from "../utils/landingLayout";
import { safeButtonTextSx, safeTextSx, textLength } from "../utils/textSafety";
import { catalogLabel, catalogListPath } from "../utils/catalogRouting";
import { normalizeImagePosition, scaleResponsiveCss, segmentCssVariables, segmentVisualScale } from "../utils/segmentVisualStyle";
import { landingSectionBackgroundSx } from "../utils/landingSectionBackground";
import { isCanonicalGastronomicCatalogNavigation } from "../utils/siteExperienceDefaults";

export default function Footer() {
  const navigate = useNavigate();
  const { productsEnabled, gastronomicMenuEnabled } = useProjectConfig();
  const landingContext = useLanding();
  const landing = landingContext?.landing || landingContext || {};
  const { business, footer, theme, sections, navigation, contact, catalog } = landing;
  const gastronomicHome = gastronomicMenuEnabled && catalog?.homeSource === "gastronomicMenu";
  const style = resolveFooterStyle(footer, theme);
  const footerTitleScale = segmentVisualScale(theme, "footer", "title");
  const footerImageScale = segmentVisualScale(theme, "footer", "image");
  const footerSpacingScale = segmentVisualScale(theme, "footer", "spacing");
  const footerVisualStyle = (Array.isArray(landing?.layout?.segments) ? landing.layout.segments : []).find((segment) => segment?.id === "footer")?.visualStyle || {};
  const footerBackgroundSx = landingSectionBackgroundSx(footerVisualStyle);
  const footerLogoPosition = normalizeImagePosition(footer?.logoPosition);
  const logo = resolveLandingMedia(theme?.logoUrl || theme?.compactLogoUrl);
  const requestedFooterLogoHeight = footer?.logoMaxHeight === "" || footer?.logoMaxHeight === null || footer?.logoMaxHeight === undefined
    ? null
    : Number(footer.logoMaxHeight);
  const footerLogoMaxHeight = Number.isFinite(requestedFooterLogoHeight)
    ? Math.min(160, Math.max(20, requestedFooterLogoHeight))
    : null;
  const footerLogoColor = String(footer?.logoColor || "").trim();
  const whatsapp = buildWhatsAppUrl(business?.whatsapp, contact?.whatsappMessage);
  const address = fullBusinessAddress(business);
  const defaultGastronomicNavigation = isCanonicalGastronomicCatalogNavigation(navigation, gastronomicMenuEnabled);
  const year = new Date().getFullYear();
  const copyright = String(
    footer?.copyright ?? "{businessName}. Todos los derechos reservados."
  )
    .replace(/\{businessName\}/g, business?.name ?? "")
    .replace(/\{year\}/g, String(year));
  const sectionLinkIsRenderable = (url, type) => {
    const target = String(url || "").trim();
    if (!target.startsWith("#")) return true;
    return hasVisibleLandingSegmentType(landing, type, productsEnabled);
  };
  const goTo = (url) => {
    const target = String(url || "").trim();
    if (target.startsWith("#") && !document.getElementById(target.slice(1))) {
      navigate("/");
      window.setTimeout(() => openConfiguredUrl(target, navigate), 80);
      return;
    }
    openConfiguredUrl(target, navigate);
  };

  const navItems = [
    {
      key: "home",
      label: footer?.homeLabel ?? navigation?.homeLabel ?? "Inicio",
      url: navigation?.homeUrl || "/",
      visible: true,
      fieldPath: "footer.homeLabel",
    },
    {
      key: "gastronomic-menu",
      label: "Carta",
      url: "/carta",
      visible: gastronomicHome && !defaultGastronomicNavigation,
    },
    {
      key: "products",
      label:
        footer?.productsLabel ??
        catalogLabel(catalog?.mode, navigation?.productsLabel),
      url: defaultGastronomicNavigation ? "/carta" : catalogListPath(catalog?.mode, navigation?.productsUrl || "/productos"),
      visible: productsEnabled && navigation?.showProductsLink !== false,
      fieldPath: "footer.productsLabel",
    },
    {
      key: "about",
      label: navigation?.aboutLabel ?? "Sobre nosotros",
      url: navigation?.aboutUrl || "#about",
      visible:
        sections?.showAbout !== false &&
        navigation?.showAboutLink !== false &&
        sectionLinkIsRenderable(navigation?.aboutUrl || "#about", "about"),
    },
    {
      key: "highlights",
      label: navigation?.highlightsLabel ?? "Por qué elegirnos",
      url: navigation?.highlightsUrl || "#highlights",
      visible:
        sections?.showHighlights !== false &&
        navigation?.showHighlightsLink !== false &&
        sectionLinkIsRenderable(navigation?.highlightsUrl || "#highlights", "highlights"),
    },
    {
      key: "contact",
      label: footer?.contactLabel ?? navigation?.contactLabel ?? "Contacto",
      url: navigation?.contactUrl || "/contact",
      visible:
        sections?.showContact !== false &&
        navigation?.showContactLink !== false &&
        sectionLinkIsRenderable(navigation?.contactUrl || "/contact", "contact"),
      fieldPath: "footer.contactLabel",
    },
  ].filter((item) => item.visible && item.label);

  const contactItems = [
    address
      ? {
          key: "address",
          label: address,
          icon: <LocationOnOutlinedIcon fontSize="small" />,
        }
      : null,
    business?.phone
      ? {
          key: "phone",
          label: business.phone,
          href: `tel:${business.phone}`,
          icon: <PhoneOutlinedIcon fontSize="small" />,
        }
      : null,
    business?.email
      ? {
          key: "email",
          label: business.email,
          href: `mailto:${business.email}`,
          icon: <EmailOutlinedIcon fontSize="small" />,
        }
      : null,
    business?.schedule
      ? {
          key: "schedule",
          label: business.schedule,
          icon: <AccessTimeOutlinedIcon fontSize="small" />,
        }
      : null,
  ].filter(Boolean);

  const socialLinks = [
    whatsapp
      ? { key: "whatsapp", label: "WhatsApp", url: whatsapp, icon: <WhatsAppIcon /> }
      : null,
    business?.socials?.instagram
      ? {
          key: "instagram",
          label: "Instagram",
          url: business.socials.instagram,
          icon: <InstagramIcon />,
        }
      : null,
    business?.socials?.facebook
      ? {
          key: "facebook",
          label: "Facebook",
          url: business.socials.facebook,
          icon: <FacebookIcon />,
        }
      : null,
    business?.socials?.youtube
      ? {
          key: "youtube",
          label: "YouTube",
          url: business.socials.youtube,
          icon: <YouTubeIcon />,
        }
      : null,
    business?.socials?.linkedin
      ? {
          key: "linkedin",
          label: "LinkedIn",
          url: business.socials.linkedin,
          icon: <LinkedInIcon />,
        }
      : null,
    business?.socials?.tiktok
      ? {
          key: "tiktok",
          label: "TikTok",
          url: business.socials.tiktok,
          icon: <MusicNoteIcon />,
        }
      : null,
    business?.socials?.website
      ? {
          key: "website",
          label: "Sitio web",
          url: business.socials.website,
          icon: <LanguageIcon />,
        }
      : null,
  ].filter(Boolean);

  const businessName = String(business?.name ?? "").trim();
  const tagline = String(footer?.tagline ?? business?.shortDescription ?? "").trim();
  const hasBrand = Boolean((logo && footer?.showLogo !== false) || businessName || tagline);
  const hasNavigation = navItems.length > 0;
  const hasContact = contactItems.length > 0;
  const hasSocials = navigation?.showSocialLinks !== false && socialLinks.length > 0;
  const hasCopyright = Boolean(copyright.trim());
  const hasVisibleFooter = hasBrand || hasNavigation || hasContact || hasSocials || hasCopyright;

  if (!hasVisibleFooter) return null;

  const Brand = ({ centered = false, inverse = false, compact = false }) => (
    <Box sx={{ minWidth: 0, maxWidth: "100%", textAlign: centered ? "center" : "left" }}>
      {logo && footer?.showLogo !== false ? (
        <Box
          component="button"
          type="button"
          onClick={() => goTo(navigation?.homeUrl || "/")}
          aria-label={business?.name ?? navigation?.homeLabel ?? "Inicio"}
          sx={{
            display: "inline-flex",
            border: 0,
            p: compact ? 0 : 1.2,
            mb: compact ? 1.2 : 1.8,
            bgcolor: compact ? "transparent" : inverse ? "rgba(255,255,255,.96)" : "#FFFFFF",
            borderRadius: compact ? 0 : 2.5,
            cursor: "pointer",
            boxShadow: compact ? "none" : "0 12px 32px rgba(0,0,0,.16)",
            maxWidth: "100%",
          }}
        >
          <BrandLogoImage
            src={logo}
            alt={business?.name ?? "Logo"}
            logoColor={footerLogoColor}
            sx={footerLogoMaxHeight !== null
              ? {
                  display: "block",
                  width: "auto",
                  maxWidth: "100%",
                  height: `${footerLogoMaxHeight}px`,
                  maxHeight: `${footerLogoMaxHeight}px`,
                  objectFit: "contain",
                  objectPosition: footerLogoPosition,
                }
              : {
                  display: "block",
                  width: { xs: `min(70vw, ${(compact ? 160 : 190) * footerImageScale}px)`, sm: (compact ? 180 : 220) * footerImageScale },
                  height: { xs: (compact ? 50 : 62) * footerImageScale, sm: (compact ? 56 : 72) * footerImageScale },
                  objectFit: "contain",
                  objectPosition: footerLogoPosition,
                }}
          />
        </Box>
      ) : businessName ? (
        <Typography
          data-landing-field="business.name"
          variant={compact ? "h5" : "h4"}
          sx={{
            ...safeTextSx,
            fontSize: scaleResponsiveCss(
              textLength(businessName) > 52
                ? { xs: "1.55rem", sm: "1.8rem", md: "2rem" }
                : compact
                  ? { xs: "1.5rem", md: "1.7rem" }
                  : { xs: "2rem", md: "2.125rem" },
              footerTitleScale
            ),
            color: inverse ? "#fff" : "#19242F",
            mb: tagline ? 1 : 0,
            fontWeight: "var(--wp-segment-title-weight, 950)",
            letterSpacing: "var(--wp-segment-letter-spacing, -.035em)",
          }}
        >
          {businessName}
        </Typography>
      ) : null}

      {tagline ? (
        <Typography
          data-landing-field={footer?.tagline ? "footer.tagline" : "business.shortDescription"}
          sx={{
            ...safeTextSx,
            maxWidth: centered ? 650 : 470,
            mx: centered ? "auto" : 0,
            color: inverse ? "rgba(255,255,255,.7)" : "#5B5D73",
            lineHeight: { xs: 1.6, md: 1.75 },
            whiteSpace: "pre-line",
          }}
        >
          {tagline}
        </Typography>
      ) : null}
    </Box>
  );

  const Navigation = ({ horizontal = false, inverse = false, centered = false }) => hasNavigation ? (
    <Box sx={{ minWidth: 0, maxWidth: "100%", textAlign: centered ? "center" : "left" }}>
      {!horizontal && footer?.navigationTitle ? (
        <Typography data-landing-field="footer.navigationTitle" variant="h6" sx={{ ...safeTextSx, color: inverse ? "#fff" : "#19242F", mb: 1.4 }}>
          {footer.navigationTitle}
        </Typography>
      ) : null}
      <Stack
        direction={horizontal ? "row" : { xs: "row", sm: "column" }}
        alignItems={horizontal || centered ? "center" : { xs: "center", sm: "flex-start" }}
        justifyContent={horizontal ? "center" : "flex-start"}
        flexWrap="wrap"
        useFlexGap
        spacing={horizontal ? 0.8 : { xs: 0.6, sm: 0.25 }}
      >
        {navItems.map((item) => (
          <Button
            key={item.key}
            data-landing-field={item.fieldPath || undefined}
            onClick={() => goTo(item.url)}
            sx={{
              ...safeButtonTextSx,
              maxWidth: "100%",
              color: inverse ? "rgba(255,255,255,.78)" : "#5B5D73",
              px: horizontal ? 1.5 : { xs: 1.1, sm: 0 },
              minWidth: 0,
              minHeight: { xs: 40, sm: "auto" },
              "&:hover": { color: inverse ? "#fff" : "#F35A3A" },
            }}
          >
            {item.label}
          </Button>
        ))}
      </Stack>
    </Box>
  ) : null;

  const ContactDetails = ({ inverse = false, centered = false, showTitle = true }) => hasContact ? (
    <Box sx={{ minWidth: 0, maxWidth: "100%", textAlign: centered ? "center" : "left" }}>
      {showTitle && footer?.contactTitle ? (
        <Typography data-landing-field="footer.contactTitle" variant="h6" sx={{ ...safeTextSx, color: inverse ? "#fff" : "#19242F", mb: 1.4 }}>
          {footer.contactTitle}
        </Typography>
      ) : null}
      <Stack spacing={1.05} alignItems={centered ? "center" : "stretch"}>
        {contactItems.map((item) => (
          <Stack
            key={item.key}
            direction="row"
            spacing={1}
            alignItems={item.key === "address" ? "flex-start" : "center"}
            justifyContent={centered ? "center" : "flex-start"}
            sx={{ minWidth: 0, maxWidth: "100%" }}
          >
            <Box sx={{ flexShrink: 0, color: inverse ? "inherit" : "#F35A3A", display: "flex", mt: item.key === "address" ? 0.2 : 0 }}>
              {item.icon}
            </Box>
            <Typography
              component={item.href ? "a" : "span"}
              href={item.href}
              sx={{
                ...safeTextSx,
                color: inverse ? "rgba(255,255,255,.74)" : "#5B5D73",
                textDecoration: "none",
                lineHeight: 1.55,
              }}
            >
              {item.label}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  ) : null;

  const Socials = ({ inverse = false, centered = false }) =>
    navigation?.showSocialLinks !== false && socialLinks.length ? (
      <Box sx={{ minWidth: 0, maxWidth: "100%", textAlign: centered ? "center" : "left" }}>
        <Typography
          data-landing-field="navigation.socialTitle"
          variant="h6"
          sx={{
            ...safeTextSx,
            color: inverse ? "#fff" : "#19242F",
            mb: 1.4,
          }}
        >
          {navigation?.socialTitle || "Redes sociales"}
        </Typography>
        <Stack
          direction="row"
          spacing={0.6}
          justifyContent={centered ? "center" : "flex-start"}
          flexWrap="wrap"
          useFlexGap
        >
          {socialLinks.map((social) => (
            <IconButton
              key={social.key}
              component="a"
              href={social.url}
              target="_blank"
              rel="noreferrer"
              aria-label={social.label}
              sx={{
                color: inverse ? "#fff" : "#19242F",
                bgcolor: inverse ? "rgba(255,255,255,.1)" : "rgba(25,36,47,.06)",
                border: "1px solid",
                borderColor: inverse ? "rgba(255,255,255,.12)" : "rgba(26,26,46,.09)",
                "&:hover": {
                  bgcolor: inverse ? "rgba(255,255,255,.18)" : "rgba(25,36,47,.1)",
                },
              }}
            >
              {social.icon}
            </IconButton>
          ))}
        </Stack>
      </Box>
    ) : null;

  const Copyright = ({ inverse = false, centered = true }) => hasCopyright ? (
    <Typography
      data-landing-field="footer.copyright"
      variant="body2"
      sx={{
        ...safeTextSx,
        color: inverse ? "rgba(255,255,255,.52)" : "#5B5D73",
        textAlign: centered ? "center" : "left",
      }}
    >
      {copyright}
    </Typography>
  ) : null;

  if (style === "style-2") {
    return (
      <Box
        component="footer"
        id="footer"
        data-landing-section="footer"
        data-footer-style={style}
        data-footer-logo-position={footerLogoPosition}
        sx={{
          ...segmentCssVariables(theme, "footer"),
          bgcolor: "#FFFFFF",
          color: "#1A1A2E",
          borderTop: "1px solid",
          borderColor: "rgba(26,26,46,.09)",
          scrollMarginTop: { xs: 72, md: 122 },
          ...footerBackgroundSx,
        }}
      >
        <Box sx={{ maxWidth: 1440, mx: "auto", px: { xs: 2, sm: 4, md: 7 }, py: { xs: 4 * footerSpacingScale, sm: 5 * footerSpacingScale, md: 6 * footerSpacingScale } }}>
          {hasBrand || hasContact || hasSocials ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: hasBrand && (hasContact || hasSocials)
                    ? "minmax(0,1.3fr) minmax(250px,.7fr)"
                    : "1fr",
                },
                alignItems: "center",
                gap: { xs: 3, md: 7 },
              }}
            >
              {hasBrand ? <Brand /> : null}
              {hasContact || hasSocials ? (
                <Box>
                  <ContactDetails showTitle={false} />
                  {hasSocials ? <Box sx={{ mt: hasContact ? 2 : 0 }}><Socials /></Box> : null}
                </Box>
              ) : null}
            </Box>
          ) : null}
          {(hasBrand || hasContact || hasSocials) && (hasNavigation || hasCopyright) ? <Divider sx={{ my: 3.5 }} /> : null}
          {hasNavigation || hasCopyright ? (
            <Stack
              direction={{ xs: "column", md: "row" }}
              alignItems="center"
              justifyContent="space-between"
              spacing={1.5}
            >
              <Navigation horizontal />
              <Copyright centered={false} />
            </Stack>
          ) : null}
        </Box>
        <Box sx={{ height: 6, bgcolor: "#F35A3A" }} />
      </Box>
    );
  }

  if (style === "style-3") {
    return (
      <Box
        component="footer"
        id="footer"
        data-landing-section="footer"
        data-footer-style={style}
        data-footer-logo-position={footerLogoPosition}
        sx={{
          ...segmentCssVariables(theme, "footer"),
          position: "relative",
          overflow: "hidden",
          bgcolor: "#10171e",
          color: "#fff",
          scrollMarginTop: { xs: 72, md: 82 },
          ...footerBackgroundSx,
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            background: `radial-gradient(circle at 100% 0%, #F35A3A 0%, transparent 34%), linear-gradient(135deg, #19242F 0%, #10171e 72%)`,
            ...(footerBackgroundSx.backgroundImage ? footerBackgroundSx : {}),
          }}
        />
        <Box sx={{ position: "relative", zIndex: 1, maxWidth: 1040, mx: "auto", px: { xs: 2, sm: 2.5 }, py: { xs: 4.5 * footerSpacingScale, sm: 6 * footerSpacingScale, md: 7 * footerSpacingScale } }}>
          {hasBrand ? <Brand centered inverse compact /> : null}
          {hasNavigation ? <Box sx={{ mt: hasBrand ? 2.5 : 0 }}><Navigation horizontal inverse centered /></Box> : null}
          {hasContact ? (
            <Box sx={{ mt: hasBrand || hasNavigation ? 3 : 0 }}><ContactDetails inverse centered showTitle={false} /></Box>
          ) : null}
          {hasSocials ? <Box sx={{ mt: hasBrand || hasNavigation || hasContact ? 3 : 0 }}><Socials inverse centered /></Box> : null}
          {hasCopyright && (hasBrand || hasNavigation || hasContact || hasSocials) ? <Divider sx={{ my: 3.5, borderColor: "rgba(255,255,255,.12)" }} /> : null}
          <Copyright inverse />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      component="footer"
      id="footer"
      data-landing-section="footer"
      data-footer-style="style-1"
      data-footer-logo-position={footerLogoPosition}
      sx={{
        ...segmentCssVariables(theme, "footer"),
        position: "relative",
        overflow: "hidden",
        bgcolor: "#19242F",
        color: "#fff",
        scrollMarginTop: { xs: 72, md: 86 },
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          opacity: 0.16,
          background: `radial-gradient(circle at 15% 0%, #F35A3A 0%, transparent 34%), radial-gradient(circle at 90% 100%, rgba(255,255,255,.35) 0%, transparent 30%)`,
          ...(footerBackgroundSx.backgroundImage ? footerBackgroundSx : {}),
        }}
      />
      <Box sx={{ position: "relative", zIndex: 1, maxWidth: 1440, mx: "auto", px: { xs: 2, sm: 4, md: 7 }, pt: { xs: 4.5 * footerSpacingScale, sm: 6 * footerSpacingScale, md: 8 * footerSpacingScale }, pb: { xs: 2.5, md: 3 } }}>
        {hasBrand || hasSocials || hasNavigation || hasContact ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(auto-fit,minmax(220px,1fr))" },
              gap: { xs: 3, md: 6 },
            }}
          >
            {hasBrand || hasSocials ? (
              <Box>
                {hasBrand ? <Brand inverse /> : null}
                {hasSocials ? <Box sx={{ mt: hasBrand ? 2.2 : 0 }}><Socials inverse /></Box> : null}
              </Box>
            ) : null}
            <Navigation inverse />
            <ContactDetails inverse />
          </Box>
        ) : null}
        {hasCopyright && (hasBrand || hasSocials || hasNavigation || hasContact) ? <Divider sx={{ my: 4, borderColor: "rgba(255,255,255,.16)" }} /> : null}
        <Copyright inverse />
      </Box>
    </Box>
  );
}
